import zod from 'zod';
import { SigmaIntegrationFormSchema } from '../../../services/IntegrationService/SigmaService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useCreateSigmaIntegrationAsync,
  useGetSigmaAccountsAsync,
  useGetSigmaIntegrationByIdAsync,
  useUpdateSigmaIntegrationAsync,
} from '../../../services/IntegrationService/SigmaService/hooks/useMutation';
import { ISigmaAccountsProps } from '../../../services/IntegrationService/SigmaService/types';
import DropdownSelectComponent from '../../../components/DropdownSelectComponent';
import TooltipComponent from '../../../components/TooltipComponent';
import { t } from 'i18next';

interface SigmaEditionFormProps {
  sigmaIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type SigmaEditionFormInput = zod.infer<typeof SigmaIntegrationFormSchema>;

const SigmaEditionForm = ({
  sigmaIdToUpdate,
  handleSaveForm,
}: SigmaEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset, getValues, watch } =
    useForm<SigmaEditionFormInput>({
      resolver: zodResolver(SigmaIntegrationFormSchema),
      defaultValues: {
        name: '',
        description: '',
        isActive: true,
        token: '',
        accounts: [],
      },
    });

  const [accountOptions, setAccountOptions] = useState<ISigmaAccountsProps[]>(
    [],
  );

  const {
    mutate: mutateCreateSigmaIntegration,
    isLoading: isLoadingCreateSigmaIntegration,
  } = useCreateSigmaIntegrationAsync();
  const {
    mutate: mutateUpdateSigmaIntegration,
    isLoading: isLoadingUpdateSigmaIntegration,
  } = useUpdateSigmaIntegrationAsync();
  const {
    mutateAsync: mutateGetSigmaIntegrationByIdAsync,
    isLoading: isLoadingGetSigmaIntegration,
  } = useGetSigmaIntegrationByIdAsync();
  const {
    mutateAsync: mutateGetSigmaAccountsAsync,
    isLoading: isLoadingGetSigmaAccountIntegration,
  } = useGetSigmaAccountsAsync();

  const handleSave = (data: SigmaEditionFormInput) => {
    const newSigmaToPersist: any = { ...data, accounts: [] };
    formatAccounts(data, newSigmaToPersist);

    if (sigmaIdToUpdate) {
      mutateUpdateSigmaIntegration(
        { id: sigmaIdToUpdate, data: newSigmaToPersist },
        {
          onSuccess: () => {
            saveIntegration('Integração sigma atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateSigmaIntegration(newSigmaToPersist, {
      onSuccess: () => {
        saveIntegration('Integração sigma criada com sucesso.');
      },
    });
  };

  const formatAccounts = (
    data: SigmaEditionFormInput,
    newSigmaToPersist: any,
  ) => {
    if (data.accounts && accountOptions.length > 0) {
      data.accounts.forEach((account) => {
        const acc = accountOptions.find((a) => a.accountCode === account);
        if (acc) newSigmaToPersist.accounts.push({ ...acc });
      });
    }
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  const token = watch('token');

  useEffect(() => {
    if (sigmaIdToUpdate) {
      mutateGetSigmaIntegrationByIdAsync(sigmaIdToUpdate).then((r) => {
        const sigmaDb: any = {
          ...r,
          accounts: r.accounts.map((ac) => ac.accountCode),
        };
        reset(sigmaDb, { keepDefaultValues: true });
      });
    }
  }, [sigmaIdToUpdate]);

  useEffect(() => {
    if (token) {
      mutateGetSigmaAccountsAsync(token).then(setAccountOptions);
    }
  }, [token]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.sigmaForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              // <TooltipComponent title={value}>
              <TextFieldComponent
                value={value}
                maxLength={2000}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.sigmaForms.token')}
                onBlur={() => {
                  mutateGetSigmaAccountsAsync(getValues('token')).then(
                    setAccountOptions,
                  );
                }}
              />
              // </TooltipComponent>
            )}
            name={'token'}
            control={control}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownSelectComponent
                value={value}
                options={accountOptions}
                labelField={'accountCode'}
                valueField={'accountCode'}
                errorMessage={error?.message}
                onChange={onChange}
                displayEmpty
                label={t(
                  'integrationsPage.sigmaForms.accountsReceivableEvents',
                )}
              />
            )}
            name={'accounts'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.sigmaForms.description')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.sigmaForms.active')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateSigmaIntegration ||
              isLoadingUpdateSigmaIntegration ||
              isLoadingGetSigmaIntegration ||
              isLoadingGetSigmaAccountIntegration
            }
          >
            {t('integrationsPage.sigmaForms.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SigmaEditionForm;
